/**
 * The board's network side: what it says over Bluetooth, and what answers at
 * the address it gives.
 *
 * "ready:<ip>:<port>" on the WIFI characteristic is the board's account of its
 * own side of the network. Whether *this* device can get there is a separate
 * fact - a guest network, another SSID, mobile data, a stale lease - so it is
 * measured with a GET /ping rather than taken on the board's word.
 *
 * Ported from ESP32-Tools 1.5.0: `parse_wifi_status()` in `bleota.py`, and
 * `_parse_ping()`, `still_coming_up()` and `probe_endpoint()` in `wifiota.py`.
 * The web front end imports `parsePing` from here too, so the check that stops
 * an image going to somebody else's web server exists once.
 *
 * No React Native imports: `fetch` is passed in, so the probe runs under node
 * against a real local HTTP server in the tests.
 */

export const PING_PATH = '/ping';
export const OTA_PATH = '/ota';
export const DEFAULT_PORT = 80;

// --------------------------------------------------------- the BLE side
/**
 * "ready:<ip>:<port>" to a host and port, or null for any other status.
 *
 * Adopt-mode firmware appends a "|..." trace to statuses it is still working
 * on. It should never follow "ready", but a port that fails to parse because
 * of a suffix would silently lose the address, so anything after the first
 * "|" is ignored regardless.
 */
export function parseWifiStatus(
  status: string | null | undefined
): { host: string; port: number } | null {
  if (!status) return null;
  const s = status.split('|', 1)[0].trim();
  if (!s.startsWith('ready:')) return null;
  const bits = s.split(':');
  if (bits.length < 3 || !/^\d+$/.test(bits[2])) return null;
  return { host: bits[1], port: Number(bits[2]) };
}

/**
 * Is this board's network still on its way up, and worth asking again soon?
 *
 * "adopting" belongs here as much as "connecting:": the OTA service is
 * waiting for the project's own station rather than for its own, but the
 * question - give it another few seconds? - has the same answer.
 */
export const stillComingUp = (status: string | null | undefined): boolean =>
  !!status && (status === 'adopting' || status.startsWith('connecting:'));

// -------------------------------------------------------- the HTTP side
export interface PingBody {
  ok: true;
  project?: string;
  version?: string;
  idf?: string;
  bt?: string;
  [key: string]: unknown;
}

/**
 * The OTA firmware's /ping answer, or null for anything else.
 *
 * Strict on purpose: a project's own web server may answer every path with
 * its single-page app - HTTP 200 and all - and the letters "ok" somewhere in
 * that HTML must not make it a board. Only a JSON object with ok === true is
 * one.
 */
export function parsePing(status: number, body: string): PingBody | null {
  if (status !== 200) return null;
  let data: unknown;
  try {
    data = JSON.parse(body);
  } catch {
    return null;
  }
  if (typeof data !== 'object' || data === null || Array.isArray(data)) return null;
  const obj = data as Record<string, unknown>;
  if (obj.ok !== true) return null;
  return obj as PingBody;
}

/** What one check of an address found. */
export interface Found {
  host: string;
  port: number;
  /** The OTA service answers here. */
  reachable: boolean;
  /**
   * "ping" - /ping answered, so project and version are known; "ota-only" -
   * firmware from before /ping, found by asking POST /ota without an image.
   */
  api?: 'ping' | 'ota-only';
  project?: string | null;
  version?: string | null;
  latencyMs?: number;
  /** When not reachable: what happened instead, in words. */
  reason?: string;
}

const text = (v: unknown): string | null => (typeof v === 'string' ? v : null);

/** The code under a fetch failure: node puts it on `cause`, RN nowhere. */
function errnoOf(e: unknown): string | undefined {
  const cause = (e as { cause?: { code?: unknown } } | null)?.cause;
  return typeof cause?.code === 'string' ? cause.code : undefined;
}

/**
 * Check an address from here, and say exactly what is there.
 *
 * The desktop's `probe_endpoint(page=False)`: GET /ping, strict about the
 * answer; failing that, an empty POST /ota, which the handler refuses with 400
 * before it goes near the flash - so a firmware from before /ping still
 * counts, and a web server that has neither is named as that rather than as
 * the board. `tries` covers a weak link dropping the odd request; an HTTP
 * answer ends it at once, since a retry would only get the same one.
 *
 * The desktop's other two requests, for a page a browser would show, are left
 * out: nothing on the phone opens one.
 */
export async function probeEndpoint(
  fetchFn: typeof fetch,
  host: string,
  port: number,
  timeoutMs = 2_500,
  tries = 2,
  pause: (ms: number) => Promise<void> = (ms) => new Promise((r) => setTimeout(r, ms))
): Promise<Found> {
  const base = port === DEFAULT_PORT ? `http://${host}` : `http://${host}:${port}`;
  const request = async (method: 'GET' | 'POST', path: string) => {
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), timeoutMs);
    try {
      const res = await fetchFn(base + path, {
        method,
        body: method === 'POST' ? '' : undefined,
        signal: ctl.signal,
        cache: 'no-store',
      });
      return { status: res.status, body: (await res.text()).slice(0, 4096) };
    } finally {
      clearTimeout(timer);
    }
  };

  const found: Found = { host, port, reachable: false };
  let reason = 'no answer';
  for (let attempt = 1; attempt <= tries; attempt++) {
    const t0 = Date.now();
    let answer: { status: number; body: string } | null = null;
    try {
      answer = await request('GET', PING_PATH);
    } catch (e) {
      const name = (e as { name?: unknown } | null)?.name;
      if (name === 'AbortError') {
        reason = `no answer within ${timeoutMs / 1000} s`;
      } else if (errnoOf(e) === 'ECONNREFUSED') {
        reason =
          'connection refused - something is at that address, but nothing ' +
          `is listening on port ${port}`;
      } else {
        reason = `not reachable (${e instanceof Error ? e.message : String(e)})`;
      }
    }
    if (answer) {
      const ms = Date.now() - t0;
      const info = parsePing(answer.status, answer.body);
      if (info) {
        return {
          ...found,
          reachable: true,
          api: 'ping',
          latencyMs: ms,
          project: text(info.project),
          version: text(info.version),
        };
      }
      let otaHandler = false;
      try {
        otaHandler = (await request('POST', OTA_PATH)).status === 400;
      } catch {
        // Nothing more to learn from a request that did not complete.
      }
      if (otaHandler) {
        return { ...found, reachable: true, api: 'ota-only', latencyMs: ms };
      }
      return {
        ...found,
        reason:
          `HTTP ${answer.status} from ${host}:${port} - a web server, but not ` +
          'the OTA service (it has neither /ping nor /ota)',
      };
    }
    if (attempt < tries) await pause(500);
  }
  return { ...found, reason };
}

/** "ble_ota_c3 v1.0.7" from what /ping said, or null when it said nothing. */
export function describeFirmware(f: Pick<Found, 'project' | 'version'>): string | null {
  if (!f.project && !f.version) return null;
  return `${f.project ?? 'firmware'} ${f.version ?? ''}`.trim();
}
